import type { SparePartRow } from "@/hooks/useSpareParts";

export interface PartType {
  id: string;
  name: string;
  item_type: string;
  brand: string | null;
  oem_part_number: string | null;
  compatible_models: string[];
  /** Configured parts categories present among this group's units. */
  parts_category_names: string[];
  unmatched: boolean;
  units: SparePartRow[];
  stock: number;
  awaiting: number;
  latest_harvested_at: string;
}

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Stable id from grouping key — URL-safe. */
export function partTypeIdFor(unit: Pick<SparePartRow, "name" | "brand" | "model_number">): string {
  const key = `${norm(unit.name)}|${norm(unit.brand)}|${norm(unit.model_number)}`;
  // simple djb2 hash → base36
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  return `pt_${(h >>> 0).toString(36)}`;
}

export function groupPartTypes(units: SparePartRow[]): PartType[] {
  const map = new Map<string, PartType>();
  for (const u of units) {
    const id = partTypeIdFor(u);
    let pt = map.get(id);
    if (!pt) {
      pt = {
        id,
        name: u.name,
        item_type: u.name,
        brand: u.brand,
        oem_part_number: null, // no column yet
        compatible_models: [],
        parts_category_names: [],
        unmatched: true,
        units: [],
        stock: 0,
        awaiting: 0,
        latest_harvested_at: u.harvested_at,
      };
      map.set(id, pt);
    }
    pt.units.push(u);
    pt.stock += 1;
    if (!u.bin_id) pt.awaiting += 1;
    if (u.model_number && !pt.compatible_models.includes(u.model_number)) {
      pt.compatible_models.push(u.model_number);
    }
    if (u.parts_category_name && !pt.parts_category_names.includes(u.parts_category_name)) {
      pt.parts_category_names.push(u.parts_category_name);
    }
    if (u.harvested_at > pt.latest_harvested_at) pt.latest_harvested_at = u.harvested_at;
  }
  return Array.from(map.values());
}
